import { useState } from "react";
import api from "@/lib/api";
import ValidationReport from "@/components/ValidationReport";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Validator() {
  const [form, setForm] = useState({ idea: "", audience: "", problem: "", solution: "", pricing: "", website: "", competitor: "" });
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.idea.trim()) { toast("Describe your idea first"); return; }
    setLoading(true); setReport(null);
    try {
      const res = await api.post("/ai/validate", form);
      setReport(res.data.report);
      toast.success("Validation report ready");
      setTimeout(() => document.getElementById("val-report")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) { toast.error(e.response?.data?.detail || "Validation failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="text-center mb-10">
        <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mx-auto mb-4 glow-cyan"><ShieldCheck className="w-6 h-6 text-cyan-400" /></div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-50">Validate My Idea</h1>
        <p className="text-slate-400 mt-2 max-w-xl mx-auto">Get a rigorous, unbiased AI validation report before you write a line of code.</p>
      </div>

      <form onSubmit={submit} className="glass rounded-2xl p-6 sm:p-8 grid md:grid-cols-2 gap-5 mb-10">
        <Field label="Product idea *" className="md:col-span-2"><Textarea value={form.idea} onChange={set("idea")} placeholder="e.g. An AI tool that writes proposals for local contractors" className="bg-white/5 border-white/10 text-slate-200" data-testid="validate-idea-input" /></Field>
        <Field label="Target audience"><Input value={form.audience} onChange={set("audience")} placeholder="e.g. Roofers, HVAC companies" className="bg-white/5 border-white/10 text-slate-200" data-testid="validate-audience-input" /></Field>
        <Field label="Pricing idea"><Input value={form.pricing} onChange={set("pricing")} placeholder="e.g. $49/month" className="bg-white/5 border-white/10 text-slate-200" data-testid="validate-pricing-input" /></Field>
        <Field label="Problem"><Textarea value={form.problem} onChange={set("problem")} placeholder="What problem does it solve?" className="bg-white/5 border-white/10 text-slate-200" /></Field>
        <Field label="Proposed solution"><Textarea value={form.solution} onChange={set("solution")} placeholder="How does it solve it?" className="bg-white/5 border-white/10 text-slate-200" /></Field>
        <Field label="Website (optional)"><Input value={form.website} onChange={set("website")} placeholder="https://" className="bg-white/5 border-white/10 text-slate-200" /></Field>
        <Field label="Known competitor (optional)"><Input value={form.competitor} onChange={set("competitor")} placeholder="e.g. Jobber" className="bg-white/5 border-white/10 text-slate-200" /></Field>
        <div className="md:col-span-2">
          <Button type="submit" disabled={loading} className="w-full h-11 bg-gradient-to-r from-cyan-500 to-indigo-500 text-slate-950 font-semibold" data-testid="ai-validator-submit-button">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Validating your idea…</> : <><Sparkles className="w-4 h-4 mr-2" />Validate My Idea</>}
          </Button>
        </div>
      </form>

      <div id="val-report" className="scroll-mt-20">
        {report && <ValidationReport report={report} />}
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return <div className={className}><Label className="text-slate-400 text-xs mb-1.5 block">{label}</Label>{children}</div>;
}
